import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Icon, Message, Modal, Table } from 'semantic-ui-react';
import { API } from '../helpers';
import RootStepUpModalV815 from './RootStepUpModalV815';
import './RootBackupV1021.css';

const endpoint = '/api/root/backup';
export const BACKUP_LIMIT_V1021 = 128 * 1024 * 1024;
const words = {
  zh: {
    title: '数据备份',
    export: '导出完整资料',
    import: '导入备份',
    exportNote:
      '备份文件未加密，完整显示已保存的渠道密钥、API Key 和其他凭据。请妥善保管，不要公开分享。',
    scope:
      '站点与渠道完整配置、模型选择与重命名、Source 倍率、基准价格、系统设置、客户、组织、权限、密码哈希及账务状态。',
    passwordNote:
      '原始密码无法还原；文件保存密码哈希，恢复后仍可使用原密码。日志、登录会话、缓存、运行中任务、环境变量和外部文件不包含在内。',
    choose: '选择备份文件',
    preview: '预览导入',
    warning: '导入会覆盖已有信息',
    warningText:
      '文件中的配置、客户、密钥、权限、价格和账务数据将整体替换对应资料，不是合并。日志不会导入；现有日志仅保留在恢复前数据库副本中，恢复后的日志从空白开始。',
    restart:
      '确认后先保存待导入文件，下次重启服务时执行。恢复前自动保留数据库副本；所有用户需重新登录。请先暂停业务并确保没有其他实例共用数据库。',
    confirm: '确认覆盖并安排导入',
    consent: '我确认已暂停业务，且只有此实例使用数据库，并接受以上覆盖范围。',
    type: '输入 OVERWRITE 确认',
    password: 'Root 当前密码',
    verify: '验证并继续',
    cancel: '取消',
    auth: '验证 Root 身份',
    pending: '导入已安排，等待重启服务',
    pendingText:
      '当前资料尚未替换。请在业务暂停期间重启服务；新启动将完成恢复。重启前可取消本次导入。',
    cancelImport: '取消待导入任务',
    applied: '上次导入已完成',
    failed: '上次导入未完成，原资料保持不变',
    rollback: '恢复前数据库副本',
    table: '资料表',
    existing: '现有记录',
    incoming: '导入记录',
    file: '备份文件',
    ready: '已导出未加密备份',
    loading: '读取备份状态…',
    retry: '重新读取',
    unavailable:
      '当前环境不能导入：仅支持单实例 SQLite，且不能启用外部日志库或 Redis。',
    generic: '操作失败，请重试。',
    invalid: '不是有效的 V10.21 Acuverse 备份文件。',
    oversized: '文件为空或超过 128 MiB。',
    authFailed: '身份验证失败，请检查 Root 密码。',
    authExpired: '身份验证已过期，请重新验证。',
    busy: '仍有未完成的请求或结算任务，请先完成处理后重试。',
    failedFile: '文件读取失败，请重新选择。',
    schema: '备份表结构与当前版本不一致，不能导入。',
    unsupported: '备份仅支持 SQLite 数据库。',
    changed: '已有待导入任务，请先取消或完成该任务。',
  },
  en: {
    title: 'Data backup',
    export: 'Export all data',
    import: 'Import backup',
    exportNote:
      'The backup is unencrypted and contains complete saved channel secrets, API keys and other credentials. Keep it private.',
    scope:
      'Complete site/channel configuration, model selection and renaming, Source multipliers, base prices, settings, customers, organizations, permissions, password hashes and financial state.',
    passwordNote:
      'Original passwords cannot be recovered. Password hashes preserve existing logins after restore. Logs, login sessions, caches, running jobs, environment variables and external files are excluded.',
    choose: 'Choose backup file',
    preview: 'Preview import',
    warning: 'Import overwrites existing information',
    warningText:
      'Configuration, customers, keys, permissions, prices and financial data in the file replace the corresponding data in full; they are not merged. Logs are not imported. Existing logs remain only in the pre-restore database copy; restored logs start empty.',
    restart:
      'Confirmation stages the file for the next service restart. A database rollback copy is retained first. All users must sign in again. Pause traffic and ensure no other instance shares this database.',
    confirm: 'Confirm overwrite and schedule import',
    consent:
      'Traffic is paused, this is the only database instance, and I accept the replacement scope.',
    type: 'Type OVERWRITE to confirm',
    password: 'Current Root password',
    verify: 'Verify and continue',
    cancel: 'Cancel',
    auth: 'Verify Root identity',
    pending: 'Import scheduled; service restart required',
    pendingText:
      'Current data has not been replaced. Restart while traffic is paused to restore the backup. You can cancel before restarting.',
    cancelImport: 'Cancel pending import',
    applied: 'Previous import completed',
    failed: 'Import failed; existing data was retained',
    rollback: 'Pre-restore database copy',
    table: 'Data table',
    existing: 'Existing records',
    incoming: 'Incoming records',
    file: 'Backup file',
    ready: 'Unencrypted backup exported',
    loading: 'Loading backup state…',
    retry: 'Reload',
    unavailable:
      'Import requires a single SQLite instance without an external log database or Redis.',
    generic: 'The operation failed. Please retry.',
    invalid: 'This is not a valid V10.21 Acuverse backup.',
    oversized: 'The file is empty or exceeds 128 MiB.',
    authFailed: 'Identity verification failed. Check the Root password.',
    authExpired: 'Identity verification expired. Verify again.',
    busy: 'Requests or settlements are still pending. Reconcile them first.',
    failedFile: 'The file could not be read. Select it again.',
    schema: 'The backup schema does not match this application.',
    unsupported: 'Backup requires a SQLite database.',
    changed: 'An import is already pending. Cancel or complete it first.',
  },
};

export const rootBackupWordsV1021 = (language) =>
  words[String(language || '').startsWith('zh') ? 'zh' : 'en'];

const RootBackupV1021 = () => {
  const { i18n } = useTranslation();
  const w = rootBackupWordsV1021(i18n?.language);
  const [state, setState] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [consent, setConsent] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [action, setAction] = useState('');
  const [password, setPassword] = useState('');
  const [factor, setFactor] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const mounted = useRef(true);
  const generation = useRef(0);
  const rawFile = useRef('');
  const input = useRef(null);

  const errorText = (code) => {
    if (code === 'backup_identity_failed') return w.authFailed;
    if (code === 'backup_identity_required') return w.authExpired;
    if (code === 'backup_pending_work_requires_reconciliation') return w.busy;
    if (code === 'backup_import_already_pending') return w.changed;
    if (code === 'backup_requires_sqlite') return w.unsupported;
    if (code === 'backup_invalid_size' || code === 'backup_too_large')
      return w.oversized;
    if (code?.includes('schema') || code?.includes('table')) return w.schema;
    if (code?.includes('invalid') || code?.includes('incompatible'))
      return w.invalid;
    return w.generic;
  };
  const unwrap = (res) => {
    if (!res?.data?.success) {
      const failure = new Error(errorText(res?.data?.code));
      failure.code = res?.data?.code;
      throw failure;
    }
    return res.data.data;
  };
  const loadState = async () => {
    try {
      const next = unwrap(await API.get(endpoint + '/state'));
      if (mounted.current) {
        setState(next);
        setError('');
      }
    } catch (e) {
      if (mounted.current) setError(e.message || w.generic);
    }
  };
  useEffect(() => {
    mounted.current = true;
    loadState();
    return () => {
      mounted.current = false;
      generation.current++;
      rawFile.current = '';
    };
    // State is loaded once; language changes do not repeat sensitive operations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choose = (event) => {
    generation.current++;
    rawFile.current = '';
    setFile(event.target.files?.[0] || null);
    setPreview(null);
    setConsent(false);
    setConfirmation('');
    setError('');
    setNotice('');
  };
  const inspect = async () => {
    const run = ++generation.current;
    setBusy(true);
    setError('');
    setPreview(null);
    try {
      if (!file?.size || file.size > BACKUP_LIMIT_V1021)
        throw new Error(w.oversized);
      const raw = await file.text();
      if (!mounted.current || run !== generation.current) return;
      // Preserve the uploaded bytes for the digest-bound authorization ticket.
      const result = unwrap(
        await API.post(endpoint + '/preview', raw, {
          headers: { 'Content-Type': 'application/json' },
          transformRequest: [(value) => value],
        })
      );
      if (!mounted.current || run !== generation.current) return;
      rawFile.current = raw;
      setPreview(result);
    } catch (e) {
      if (mounted.current && run === generation.current)
        setError(e.message || w.failedFile);
    } finally {
      if (mounted.current && run === generation.current) setBusy(false);
    }
  };
  const start = (next) => {
    setPassword('');
    setError('');
    setNotice('');
    setAction(next);
  };
  const close = () => {
    if (busy) return;
    setAction('');
    setPassword('');
    setFactor(false);
  };
  const execute = async () => {
    if (!password || busy) return;
    setBusy(true);
    setError('');
    try {
      const verified = unwrap(
        await API.post(endpoint + '/verify', {
          password,
          action,
          digest: action === 'import' ? preview?.digest : '',
        })
      );
      if (!mounted.current) return;
      setPassword('');
      const headers = { 'X-Backup-Authorization': verified.ticket };
      if (action === 'export') {
        const res = await API.post(
          endpoint + '/export',
          {},
          {
            headers,
            responseType: 'text',
            transformResponse: [(value) => value],
          }
        );
        if (!mounted.current) return;
        if (!res?.headers?.['content-disposition']?.includes('attachment')) {
          let code;
          try {
            code = JSON.parse(res?.data || '{}').code;
          } catch (_) {}
          throw new Error(errorText(code));
        }
        const blob = new Blob([res.data], {
          type: 'application/json;charset=utf-8',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download =
          'alfredapi-v11.0-' +
          new Date().toISOString().replace(/[:.]/g, '-') +
          '.alfredapi.json';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        setNotice(w.ready);
      } else if (action === 'import') {
        if (
          !consent ||
          confirmation !== 'OVERWRITE' ||
          !preview ||
          !rawFile.current
        )
          throw new Error(w.invalid);
        unwrap(
          await API.post(endpoint + '/import', rawFile.current, {
            headers: {
              ...headers,
              'Content-Type': 'application/json',
              'X-Backup-Confirmation': confirmation,
            },
            transformRequest: [(value) => value],
          })
        );
        if (!mounted.current) return;
        rawFile.current = '';
        setPreview(null);
        setFile(null);
        if (input.current) input.current.value = '';
        await loadState();
      } else {
        unwrap(await API.post(endpoint + '/cancel', {}, { headers }));
        if (!mounted.current) return;
        await loadState();
      }
      setAction('');
    } catch (e) {
      if (!mounted.current) return;
      if (e.code === 'step_up_required') setFactor(true);
      else {
        setPassword('');
        setError(e.message || w.generic);
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const pending = state?.state === 'pending';
  return (
    <div className='root-backup-v1021'>
      <h2>{w.title}</h2>
      {error && (
        <Message negative role='alert'>
          {error}
        </Message>
      )}
      {notice && (
        <Message positive role='status'>
          {notice}
        </Message>
      )}
      {!state && (
        <Message>
          {w.loading}{' '}
          <Button
            size='small'
            icon='refresh'
            content={w.retry}
            onClick={loadState}
          />
        </Message>
      )}
      {pending && (
        <Message warning role='status'>
          <Message.Header>{w.pending}</Message.Header>
          <p>{w.pendingText}</p>
          <Button
            icon='cancel'
            content={w.cancelImport}
            disabled={busy}
            onClick={() => start('cancel')}
          />
        </Message>
      )}
      {state?.state === 'applied' && (
        <Message positive>
          <Message.Header>{w.applied}</Message.Header>
          <p>
            {w.rollback}: <code>{state.rollback_db}</code>
          </p>
        </Message>
      )}
      {state?.state === 'failed' && (
        <Message negative role='alert'>
          <Message.Header>{w.failed}</Message.Header>
          <p>{errorText(state.failure)}</p>
        </Message>
      )}
      <section>
        <Message warning icon>
          <Icon name='lock open' />
          <Message.Content>{w.exportNote}</Message.Content>
        </Message>
        <p>{w.scope}</p>
        <p>{w.passwordNote}</p>
        <Button
          primary
          icon='download'
          content={w.export}
          disabled={busy || !state}
          onClick={() => start('export')}
        />
      </section>
      <section>
        <h3>{w.import}</h3>
        <Message warning>
          <Message.Header>{w.warning}</Message.Header>
          <p>{w.warningText}</p>
          <p>{w.restart}</p>
        </Message>
        {state && !state.import_available && (
          <Message negative>{w.unavailable}</Message>
        )}
        <Form>
          <Form.Field>
            <label htmlFor='root-backup-file-v1021'>{w.file}</label>
            <input
              ref={input}
              id='root-backup-file-v1021'
              type='file'
              accept='.json,application/json'
              disabled={busy || pending || !state?.import_available}
              onChange={choose}
            />
          </Form.Field>
          <Button
            type='button'
            icon='file alternate outline'
            content={w.preview}
            loading={busy && !action}
            disabled={!file || busy || pending || !state?.import_available}
            onClick={inspect}
          />
          {preview && (
            <>
              <div className='root-backup-table-v1021'>
                <Table compact unstackable>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>{w.table}</Table.HeaderCell>
                      <Table.HeaderCell textAlign='right'>
                        {w.existing}
                      </Table.HeaderCell>
                      <Table.HeaderCell textAlign='right'>
                        {w.incoming}
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {preview.tables.map((row) => (
                      <Table.Row key={row.name}>
                        <Table.Cell>{row.name}</Table.Cell>
                        <Table.Cell textAlign='right'>
                          {row.existing}
                        </Table.Cell>
                        <Table.Cell textAlign='right'>
                          {row.incoming}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
              <Form.Checkbox
                id='root-backup-consent-v1021'
                label={w.consent}
                checked={consent}
                disabled={busy}
                onChange={(_, data) => setConsent(data.checked)}
              />
              <Form.Input
                id='root-backup-confirmation-v1021'
                label={w.type}
                value={confirmation}
                disabled={busy}
                autoComplete='off'
                onChange={(_, data) => setConfirmation(data.value)}
              />
              <Button
                negative
                type='button'
                icon='upload'
                content={w.confirm}
                disabled={busy || !consent || confirmation !== 'OVERWRITE'}
                onClick={() => start('import')}
              />
            </>
          )}
        </Form>
      </section>
      <Modal
        open={Boolean(action) && !factor}
        onClose={close}
        size='tiny'
        closeOnDimmerClick={!busy}
        closeOnEscape={!busy}
      >
        <Modal.Header>{w.auth}</Modal.Header>
        <Modal.Content>
          {error && (
            <Message negative role='alert'>
              {error}
            </Message>
          )}
          <Form onSubmit={execute}>
            <Form.Input
              id='root-backup-password-v1021'
              label={w.password}
              type='password'
              autoComplete='current-password'
              value={password}
              disabled={busy}
              onChange={(_, data) => setPassword(data.value)}
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button content={w.cancel} onClick={close} disabled={busy} />
          <Button
            primary
            icon='check'
            content={w.verify}
            onClick={execute}
            loading={busy}
            disabled={busy || !password}
          />
        </Modal.Actions>
      </Modal>
      <RootStepUpModalV815
        open={factor}
        onClose={() => {
          setFactor(false);
          setPassword('');
        }}
        onVerified={() => {
          setFactor(false);
          window.setTimeout(() => execute(), 0);
        }}
      />
    </div>
  );
};

export default RootBackupV1021;
